import { Grid, TextField } from "@mui/material";
import { MprcContainer } from "components";
import { AddressModel } from "generated/CoreApi_v1";
import { useSimpleDebounce } from "hooks/useDebounce";
import LibUtils from "lib/LibUtils";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

////////////////////////////////////////////////////////////////////////////////

interface IPros {
    address: AddressModel;
    required: boolean;
    error?: boolean;
    helperText?: React.ReactNode;
    readonly?: boolean;
    onFormUpdate: (dto: AddressModel | null) => void;
}

////////////////////////////////////////////////////////////////////////////////

export function getEmptyAddressModel(): AddressModel {
    return {
        address1: "",
        address2: null,
        city: "",
        state: "",
        zipCode: ""
    };
}

/////////////////////////////////////////////////////////////////////////////////

export const EditMeetingAddressBoxComponent: React.FC<IPros> = (props) => {
    const { address, error, helperText, required, readonly, onFormUpdate } = props;

    const { t } = useTranslation("lms");

    /////////////////////////////////////////////////////////////////////////////////

    const updateRecord = (name: keyof AddressModel, v: any) => {
        let newValue = { ...address, [name]: v };
        if (newValue.address1.length == 0)
            onFormUpdate(null);
        else
            onFormUpdate(newValue);
    };

    ////////////////////////////////////////////////////////////////////////////

    const [address1, setAddress1] = useState(address.address1 ?? "");
    const [address2, setAddress2] = useState(address.address2 ?? "");
    const [zip, setZip] = useState(address.zipCode ?? "");
    const [city, setCity] = useState(address.city ?? "");
    const [state, setState] = useState(address.state ?? "");

    const newAddress1 = useSimpleDebounce(address1, 200);
    const newAddress2 = useSimpleDebounce(address2, 200);
    const newZipCode = useSimpleDebounce(zip, 200);
    const newCity = useSimpleDebounce(city, 200);
    const newState = useSimpleDebounce(state, 200);

    useEffect(() => updateRecord("address1", newAddress1), [newAddress1]);
    useEffect(() => updateRecord("address2", newAddress2), [newAddress2]);
    useEffect(() => updateRecord("zipCode", newZipCode), [newZipCode]);
    useEffect(() => updateRecord("city", newCity), [newCity]);
    useEffect(() => updateRecord("state", newState), [newState]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            <TextField
                fullWidth
                required={required}
                disabled={readonly}
                name="address1"
                label={t("Settings.MeetingRoom.Address1Label")}
                margin="dense"
                slotProps={{
                    input: {
                        inputProps: { maxLength: 255 }
                    }
                }}
                value={address1}
                onChange={(e) => setAddress1(e.target.value)}
                error={error}
                helperText={helperText}
            />
            {address.address1?.trim().length > 0 &&
                <TextField
                    fullWidth
                    name="address2"
                    disabled={readonly}
                    label={t("Settings.MeetingRoom.Address2Label")}
                    margin="dense"
                    slotProps={{
                        input: {
                            inputProps: { maxLength: 255 }
                        }
                    }}
                    value={address2}
                    onChange={(e) => setAddress2(e.target.value)}
                />
            }
            {address.address1?.trim().length > 0 &&
                <MprcContainer>
                    <Grid container spacing={2} alignItems="start">
                        <Grid size={{ xs: 2 }}>
                            <TextField
                                name="zipcode"
                                fullWidth
                                disabled={readonly}
                                label={t("Settings.MeetingRoom.ZipCodeLabel")}
                                margin="dense"
                                slotProps={{
                                    input: {
                                        inputProps: { maxLength: 9 }
                                    }
                                }}
                                value={zip}
                                onChange={(e) => setZip(e.target.value)}
                            />
                        </Grid>
                        <Grid size={{ xs: 8 }}>
                            <TextField
                                fullWidth
                                disabled={readonly}
                                label={t("Settings.MeetingRoom.CityLabel")}
                                margin="dense"
                                slotProps={{
                                    input: {
                                        inputProps: { maxLength: 255 }
                                    }
                                }}
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                            />
                        </Grid>
                        <Grid size={{ xs: 2 }}>
                            <TextField
                                fullWidth
                                disabled={readonly}
                                label={t("Settings.MeetingRoom.StateLabel")}
                                margin="dense"
                                slotProps={{
                                    input: {
                                        inputProps: { maxLength: 2 }
                                    }
                                }}
                                value={state}
                                onChange={(e) => setState(e.target.value)}
                            />
                        </Grid>
                    </Grid>
                </MprcContainer>
            }
        </>
    );
};

export const EditMeetingAddressBox = React.memo(EditMeetingAddressBoxComponent, (prev, next) => {
    return prev.required == next.required
        && prev.readonly == next.readonly
        && prev.helperText == next.helperText
        && LibUtils.deepCompare(prev.address, next.address)
        ;
});
